import type { Document } from '@/hooks/useDocuments';
import type { ColumnDef } from "@tanstack/react-table"
import { AnalysisSetBadge, visibleAnalysisSetStatus } from '@/components/deep-analysis/AnalysisSetBadge';
import { DocumentActionsDropdown } from '../shared/DocumentActionsDropdown';
import type { DocumentDeleteOptions } from '@/types/document';
import {
  createSelectionColumn,
  createFileTypeColumn,
  createDateColumn,
  createFileNameColumn,
  createSummaryColumn,
  createTableCheckboxColumn,
  createOpenInNewTabColumn,
  type SelectionColumnConfig,
} from './column-builders';

export const ColumnsCaseFiles = ({
  refreshData,
  handleDelete,
  folderGuid,
  slug,
  openDocumentPanel,
  deepAnalysisEnabled = false,
  fileNameLabel,
}: {
  refreshData: () => void;
  documentTypeSlug: string;
  handleDelete: (documentIds: string[], options?: DocumentDeleteOptions) => Promise<void>;
  folderGuid?: string;
  slug: string | undefined;
  deepAnalysisEnabled?: boolean;
  fileNameLabel?: string;
  openDocumentPanel: (params: {
    documentGuid: string;
    citationNumber?: string;
    chunkId?: number;
    activeTab?: 'overview' | 'document';
  }) => void;
}) => {
  const handleDocumentClick = (documentId: string) => {
    openDocumentPanel({
      documentGuid: documentId,
      activeTab: 'document',
    });
  };

  const linkConfig = { slug, onDocumentClick: handleDocumentClick };

  const columns: ColumnDef<Document>[] = [
    createTableCheckboxColumn(),
    createFileTypeColumn({
      linkConfig,
      showExpandChevron: true,
      iconOverlay: (document) => {
        if (!deepAnalysisEnabled) return null;
        const status = visibleAnalysisSetStatus(document);
        if (!document.analysisSetEffective && !status) return null;
        return (
          <AnalysisSetBadge
            active={document.analysisSetEffective}
            status={status}
            compact
            iconOnly
          />
        );
      },
    }),
    createDateColumn(),
    createFileNameColumn({ linkConfig, label: fileNameLabel }),
    createSummaryColumn({ linkConfig }),
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex flex-row items-center justify-end gap-1">
          <DocumentActionsDropdown
            document={row.original}
            slug={slug || ''}
            fromPath={`case-files/${folderGuid ? `folder/${folderGuid}` : 'all-files'}`}
            onRefresh={refreshData}
            isCorrespondence={false}
            isBundle={row.original.document_type?.name === 'Bundles'}
            handleDelete={handleDelete}
            deepAnalysisEnabled={deepAnalysisEnabled}
          />
        </div>
      ),
    },
  ];

  // Add meta properties to each column that should use normal click behavior
  columns.forEach(column => {
    if (column.id !== 'id' && column.id !== 'actions' && column.id !== 'expand') {
      column.meta = {
        ...column.meta,
        enableClickToCopy: false,
        enableRowDoubleClick: true
      };
    }
  });

  return columns;
}

/**
 * Selection-mode columns for DocumentSelector component.
 * Uses shared column builders for consistency and reduced duplication.
 * - Uses external selection state (isSelected/onToggleSelection)
 * - No actions column
 * - No expand column
 * - File type icons open documents in new tab when clicked
 * - Row click handles selection
 */
export const ColumnsCaseFilesSelectionMode = (
  config: SelectionColumnConfig
): ColumnDef<Document>[] => {
  return [
    createSelectionColumn(config),
    createFileTypeColumn({
      isSelectionMode: true,
      matterSlug: config.matterSlug,
      getDocumentStatusIcons: config.getDocumentStatusIcons,
      iconOverlay: config.getDocumentIconOverlay,
    }),
    createDateColumn(),
    createFileNameColumn(),
    createSummaryColumn(),
    createOpenInNewTabColumn(config.matterSlug),
  ];
};
